import Config from "@_configs/api.config";
import {
  parseJwt,
  authHeader,
  getUserDataFromJwt,
  clearToken,
} from "./helpers";
import { errors } from "@_constants/errors.constants";
import config from "../config.json";

const MAX_WAIT_TIME = 10000; // 10 seconds
const RENEWAL_RETRY_DELAY = 500; // 500ms between retries

let globalAbortController = new AbortController();

function withAbortSignal(options = {}) {
  return {
    ...options,
    signal: globalAbortController.signal,
  };
}

function clearRenewalState() {
  localStorage.removeItem("isRenewing");
}

function clearAuthState() {
  clearRenewalState();
  clearToken();
  localStorage.removeItem("invalid"); // Clear the invalid flag
  globalAbortController.abort();
  window.location.href = config.ACCOUNT_PORTAL_URL + "/login?m=rs";
}

async function waitForTokenRenewal() {
  let waitTime = 0;

  while (localStorage.getItem("isRenewing") && waitTime < MAX_WAIT_TIME) {
    await new Promise((resolve) => setTimeout(resolve, RENEWAL_RETRY_DELAY));
    waitTime += RENEWAL_RETRY_DELAY;
  }

  if (waitTime >= MAX_WAIT_TIME) {
    alert(
      "Session renewal is taking longer than expected. Please log in again.",
    );
    clearAuthState();
    throw new Error("Token renewal timeout");
  }
}

/**
 * Call to renew token endpoint
 */
async function renew() {
  const localStorageUser = JSON.parse(localStorage.getItem("user") || "{}");

  if (!localStorageUser.accessToken) {
    throw new Error("No token available for renewal");
  }

  const jwtData = parseJwt(localStorageUser.accessToken);
  await callRenewAndUpdateToken(localStorageUser.email, jwtData.renew_token);
}

async function callRenewAndUpdateToken(email, renewToken) {
  try {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, renew_token: renewToken }),
    };

    const response = await fetch(
      `${Config.API_ROOT}/login/renew`,
      withAbortSignal(requestOptions),
    );

    if (!response.ok) {
      throw new Error(`Token renewal failed with status: ${response.status}`);
    }

    const text = await response.text();

    if (!text) {
      throw new Error("Token renewal returned empty response");
    }

    let user;
    try {
      user = JSON.parse(text);
    } catch (parseError) {
      throw new Error(`Invalid token response: ${parseError.message}`);
    }

    if (!user?.accessToken) {
      throw new Error("Token renewal returned no accessToken");
    }

    user.email = email;
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("jwtData", JSON.stringify(getUserDataFromJwt()));
  } catch (error) {
    alert("Token renewal failed. Please log in again.");
    clearAuthState();
    throw error;
  }
}

/**
 * Fetch helper wraps all requests of services
 * to handle a token timeout.
 *
 * If timout occurs, all reqeusts will be queued until
 * token refresh is finished. All Queued requests will be
 * resend with new token.
 *
 *
 */
class fetch_helper {
  constructor() {
    this.renew = renew;
  }

  /**
   * Fetch Wrapper which should used in every service call.
   *
   * @param {string} url Requets URL of the fetch
   * @param {object} options Request options
   * @returns {Response} Response object
   */
  async fetch(url, options) {
    try {
      // first do the real fetch call
      const requestOptions = withAbortSignal(options);
      const resp = await fetch(url, requestOptions);

      // if response is not OK throw Error to go into catch.
      // (409 will not throw error by itself)
      if (!resp.ok) throw resp;

      // if there was a renew before we can not delete because
      // call was successfull
      localStorage.removeItem("isRenewing");

      return resp;
    } catch (e) {
      if (e?.name === "AbortError" || globalAbortController.signal.aborted) {
        throw e;
      }
      // handle 401 status:
      if (e.status === 401) {
        // Already renewing - wait for completion
        if (localStorage.getItem("isRenewing")) {
          await waitForTokenRenewal();

          const newAuthForWaiters = authHeader();
          const optionsWithNewAuth = {
            ...options,
            headers: {
              ...options?.headers,
              Authorization: newAuthForWaiters.Authorization,
            },
          };

          // now we try again with the new token
          return await fetch(url, withAbortSignal(optionsWithNewAuth));
        }

        // First auth error - set renewing flag
        localStorage.setItem("isRenewing", "true");

        try {
          const responseBody = await e.json();
          const errorMessage = responseBody.error_message || "";

          // Handle INVALID TOKEN - immediate logout
          if (errorMessage === errors.AUTH_FAILURE_INVALID_TOKEN) {
            let alertMessage = "Invalid token. Please login again.";
            if (!localStorage.getItem("invalid")) {
              localStorage.setItem("invalid", "true");
              console.log("error message: ", errorMessage);
              alertMessage =
                "You have been logged out. Someone else might have logged in with the same account";
            }
            alert(alertMessage);
            clearAuthState();
            throw new Error("Invalid token. Please login again.");
          }

          // Handle EXPIRED TOKEN or missing token - attempt renewal
          if (errorMessage === errors.AUTH_FAILURE_TOKEN_EXPIRED) {
            // Check if we have a token to renew
            const user = JSON.parse(localStorage.getItem("user") || "{}");

            if (!user.accessToken) {
              alert("No authentication token found. Please log in again.");
              clearAuthState();
              throw new Error("No authentication token. Please login again.");
            }

            try {
              await renew();

              const newAuth = authHeader();
              const optionsWithNewAuth = {
                ...options,
                headers: {
                  ...options?.headers,
                  Authorization: newAuth.Authorization,
                },
              };

              const retriedResp = await fetch(
                url,
                withAbortSignal(optionsWithNewAuth),
              );
              localStorage.removeItem("isRenewing");
              return retriedResp;
            } catch (renewError) {
              let alertMessage =
                "Retrying response failed. Please login again.";
              if (!localStorage.getItem("invalid")) {
                localStorage.setItem("invalid", "true");
                alertMessage = "Your session has expired. Please log in again.";
              }
              alert(alertMessage);
              clearAuthState();
              throw new Error("Retrying response failed. Please login again.");
            }
          }

          clearRenewalState();
          throw e;
        } catch (parseError) {
          if (
            parseError?.name === "AbortError" ||
            globalAbortController.signal.aborted
          ) {
            throw parseError;
          }
          alert("Authentication error. Please log in again.");
          console.log(
            "Error parsing authentication error response: ",
            parseError,
          );
          clearAuthState();
          throw new Error("Authentication required. Please login again.");
        }
      } else {
        // Non-authentication errors (400, 404, 500, etc.)
        const status = e?.status ?? "NETWORK/UNKNOWN";
        let message = e?.statusText || e?.message || "Request failed";

        try {
          const body = await e.json();
          message = body.error_message || body.message || message;
        } catch (_) {}

        console.error(`Request failed: ${status} - ${message}`);
        throw Object.assign(new Error(message), { status });
      }
    }
  }
}
//TODO
// eslint-disable-next-line
export default new fetch_helper();
